/**
 * useAuth Hook
 * Custom hook for authentication operations
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { login as loginService } from '@/api/services/authService';
import { ROUTE_PATHS } from '@/utils/constants';
import type { LoginResponse } from '@/types/api.types';

export function useAuth() {
  const navigate = useNavigate();
  const { login: setAuthData, logout: clearAuthData, isAuthenticated, token } = useAuthStore();

  /**
   * Login with policy number and PIN
   */
  const login = useCallback(
    async (policyNumber: string, pin: string): Promise<LoginResponse> => {
      try {
        // Call API
        const response = await loginService(policyNumber, pin);

        // Store auth data
        setAuthData(response);

        // Navigate to balance page
        navigate(ROUTE_PATHS.BALANCE);

        return response;
      } catch (error) {
        // Clear any existing auth data on error
        clearAuthData();
        throw error;
      }
    },
    [setAuthData, clearAuthData, navigate]
  );

  /**
   * Logout and redirect to login page
   */
  const logout = useCallback(() => {
    clearAuthData();
    navigate(ROUTE_PATHS.LOGIN);
  }, [clearAuthData, navigate]);

  return {
    login,
    logout,
    isAuthenticated,
    token,
  };
}
